/**
 * Edit the internal-tracking journey phrases.
 *
 * The suggestion engine reads these stages from the DB, so staff/admins can
 * reword a phase, change its timing, add a new phase, or deactivate one — all
 * without a code change. {destHub} and {eta} placeholders are filled per shipment.
 */
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useNotification } from "context/notification.context";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import {
  getTrackingStages,
  addTrackingStage,
  updateTrackingStage,
  deleteTrackingStage,
  getPhraseQueue,
  resolvePhraseQueue,
} from "network/service";

const STATUS_OPTIONS = ["Pending", "Picked Up", "Intransit", "On Hold", "Delivered", "Issue"];
const blank = { stage_id: "", seq: 0, day: 0, status: "Intransit", message: "", repeatable: false, keywords: [], is_active: true };

export default function PhrasesModal(props) {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});

  const load = () => {
    setLoading(true);
    getTrackingStages()
      .then((r) => setRows((r.data || []).slice().sort((a, b) => (a.seq || 0) - (b.seq || 0))))
      .catch(() => showNotification("error", "Error", "Failed to load phrases"))
      .finally(() => setLoading(false));
    getPhraseQueue().then((r) => setQueue(r.data || [])).catch(() => {});
  };

  const resolveQueued = (q, action) => {
    resolvePhraseQueue({ id: q._id, action })
      .then(() => {
        showNotification("success", action === "add" ? "Added" : "Ignored", action === "add" ? "Phrase added to the journey" : "Phrase ignored");
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Failed"));
  };

  useEffect(() => {
    if (props.isopen) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isopen]);

  const setField = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = (row, i) => {
    const payload = {
      stage_id: row.stage_id,
      seq: Number(row.seq) || 0,
      day: Number(row.day) || 0,
      status: row.status,
      message: row.message,
      repeatable: !!row.repeatable,
      keywords: Array.isArray(row.keywords) ? row.keywords : String(row.keywords || "").split(",").map((k) => k.trim()).filter(Boolean),
      is_active: row.is_active !== false,
    };
    setSaving((s) => ({ ...s, [i]: true }));
    const req = row._id ? updateTrackingStage(row._id, payload) : addTrackingStage(payload);
    req
      .then(() => {
        showNotification("success", "Saved", `Phrase "${row.stage_id}" saved`);
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Save failed"))
      .finally(() => setSaving((s) => ({ ...s, [i]: false })));
  };

  const remove = (row) => {
    if (!row._id) return setRows((rs) => rs.filter((r) => r !== row));
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete phrase "${row.stage_id}"?`)) return;
    deleteTrackingStage(row._id)
      .then(() => load())
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Delete failed"));
  };

  const addRow = () => setRows((rs) => [...rs, { ...blank, seq: (rs.reduce((m, r) => Math.max(m, r.seq || 0), 0) || 0) + 1 }]);

  const kwStr = (row) => (Array.isArray(row.keywords) ? row.keywords.join(", ") : row.keywords || "");

  return (
    <Dialog open={props.isopen} onClose={props.handleclose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Tracking phrases
        <MDTypography variant="caption" display="block" color="text">
          The engine suggests these in order by day. Placeholders: <b>{"{destHub}"}</b>, <b>{"{eta}"}</b>.
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowX: "auto" }}>
        {queue.length > 0 && (
          <MDBox mb={2} p={1.5} sx={{ backgroundColor: "#fff8e1", borderRadius: 2, border: "1px solid #ffe0a3" }}>
            <MDTypography variant="button" fontWeight="bold">
              Learning queue — {queue.length} phrase{queue.length > 1 ? "s" : ""} the engine doesn&apos;t know yet
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Wording your staff published (from this console or the old status popup) that doesn&apos;t match any phase.
              Add it so the engine suggests it in future, or ignore it.
            </MDTypography>
            <Divider />
            {queue.map((q) => (
              <MDBox key={q._id} display="flex" alignItems="center" gap={1} mb={0.75} flexWrap="wrap">
                <Chip size="small" label={`×${q.count || 1}`} />
                <MDTypography variant="caption" fontWeight="medium" sx={{ flex: 1, minWidth: 200 }}>
                  {q.message}
                </MDTypography>
                <MDTypography variant="caption" color="text">{q.status || ""} {q.vendor ? `· ${q.vendor}` : ""}</MDTypography>
                <MDButton size="small" color="success" onClick={() => resolveQueued(q, "add")}>
                  <Icon>add</Icon>&nbsp;Add as phrase
                </MDButton>
                <MDButton size="small" color="secondary" variant="outlined" onClick={() => resolveQueued(q, "ignore")}>
                  Ignore
                </MDButton>
              </MDBox>
            ))}
          </MDBox>
        )}
        <Table size="small">
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {["Seq", "Stage id", "Day", "Status", "Message", "Match keywords", "Repeat", "Active", ""].map((h) => (
                <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row._id || `new-${i}`}>
                <TableCell sx={{ width: 60 }}>
                  <MDInput type="number" value={row.seq} onChange={(e) => setField(i, { seq: e.target.value })} />
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  <MDInput value={row.stage_id} onChange={(e) => setField(i, { stage_id: e.target.value })} />
                </TableCell>
                <TableCell sx={{ width: 60 }}>
                  <MDInput type="number" value={row.day} onChange={(e) => setField(i, { day: e.target.value })} />
                </TableCell>
                <TableCell sx={{ width: 120 }}>
                  <Select size="small" value={row.status} sx={{ height: 34, minWidth: 110 }} onChange={(e) => setField(i, { status: e.target.value })}>
                    {STATUS_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell sx={{ minWidth: 240 }}>
                  <MDInput fullWidth value={row.message} onChange={(e) => setField(i, { message: e.target.value })} />
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <MDInput fullWidth value={kwStr(row)} onChange={(e) => setField(i, { keywords: e.target.value })} />
                </TableCell>
                <TableCell>
                  <Switch checked={!!row.repeatable} onChange={(e) => setField(i, { repeatable: e.target.checked })} />
                </TableCell>
                <TableCell>
                  <Switch checked={row.is_active !== false} onChange={(e) => setField(i, { is_active: e.target.checked })} />
                </TableCell>
                <TableCell>
                  <MDBox display="flex" gap={0.5}>
                    <MDButton size="small" color="success" disabled={saving[i]} onClick={() => save(row, i)}>
                      <Icon>save</Icon>
                    </MDButton>
                    <MDButton size="small" color="error" onClick={() => remove(row)}>
                      <Icon>delete</Icon>
                    </MDButton>
                  </MDBox>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {loading && <MDTypography variant="caption" color="text">Loading…</MDTypography>}
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={addRow}>
          <Icon>add</Icon>&nbsp;Add phrase
        </MDButton>
        <MDButton variant="text" color="secondary" onClick={props.handleclose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}
